import styles from './BannerShield.module.css';

// Design handoff (Login.dc.html scShieldEl): a shield floating inside three concentric rings,
// two of which carry orbiting feature badges. The 797 viewBox is the design's own.
const C = 398.232;

type Shape = readonly ['rect', number, number, number, number, number] | string;

const ICONS: Record<string, readonly Shape[]> = {
  lock: [['rect', 4, 11, 16, 10, 2], 'M8 11V7a4 4 0 0 1 8 0v4', 'M12 15v2'],
  bug: ['M8 10h8v5a4 4 0 0 1-8 0z', 'M9.5 10V8.5a2.5 2.5 0 0 1 5 0V10', 'M4 13h4M16 13h4M5 8l3 2M19 8l-3 2M5 19l3-2M19 19l-3-2'],
  code: ['m8 8-4 4 4 4', 'm16 8 4 4-4 4', 'm14 5-4 14'],
  pkg: ['M12 3 3.5 7.5v9L12 21l8.5-4.5v-9z', 'M3.5 7.5 12 12l8.5-4.5', 'M12 12v9'],
};

const RINGS = [
  { r: 228.5, opacity: 1, seconds: 22, reverse: true, dots: [{ deg: 148, icon: 'lock' }, { deg: 20, icon: 'pkg' }] },
  { r: 348.6, opacity: 0.8, seconds: 34, reverse: false, dots: [{ deg: 225, icon: 'code' }, { deg: 86, icon: 'bug' }] },
  { r: 485, opacity: 0.4, seconds: 0, reverse: false, dots: [] },
] as const;

const SHIELD_PATH =
  'M888.78,290.27c-.1-8.78-6.73-16.13-15.45-17.1-114.32-12.63-222.83-56.05-303.43-121.03-3.2-2.58-7.08-3.87-10.95-3.87s-7.75,1.29-10.95,3.87c-80.6,64.98-189.11,108.4-303.43,121.03-8.72.97-15.34,8.32-15.45,17.1-1.71,149.15-4.84,272.42,47.76,384.73,58.53,124.97,160.92,219.4,276.75,255.9,1.73.55,3.53.83,5.32.83s3.59-.28,5.33-.83c115.82-36.5,218.21-130.93,276.74-255.9,52.6-112.31,49.47-235.58,47.76-384.73ZM713.06,453.44l-154.11,176.08-22.45,25.65c-5.05,5.76-12.33,9.06-19.99,9.06s-14.94-3.3-19.98-9.06l-91.69-104.76c-4.67-5.34-6.98-12.17-6.51-19.25s3.67-13.55,9-18.22c4.85-4.24,11.06-6.57,17.49-6.57,7.66,0,14.94,3.3,19.99,9.06l71.7,81.93,42.44-48.49,114.15-130.41c5.04-5.76,12.32-9.06,19.99-9.06,6.43,0,12.64,2.33,17.48,6.57,11.02,9.64,12.14,26.45,2.49,37.47Z';

const SCALE = (52 / 24) * 0.75;

export function BannerShield() {
  return (
    <svg viewBox="0 0 797 797" width="100%" height="100%" className={styles.svg} aria-hidden="true">
      <defs>
        <filter id="vsBadgeShadow" x="-100%" y="-100%" width="300%" height="300%">
          <feDropShadow dx="0" dy="6" stdDeviation="7" floodColor="#5a0606" floodOpacity="0.28" />
        </filter>
        <filter id="vsShadow" x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="14" dy="16" stdDeviation="11" floodColor="#5a0606" floodOpacity="0.35" />
        </filter>
      </defs>

      {RINGS.map((ring) => (
        <g key={ring.r}>
          <circle cx={C} cy={C} r={ring.r} fill="none" stroke="#fff" strokeOpacity={ring.opacity} strokeWidth="1.5" />
          {ring.dots.length ? (
            <g
              className={`${styles.orbit} ${ring.reverse ? styles.orbitReverse : ''}`}
              style={{ ['--dur' as string]: `${ring.seconds}s` }}
            >
              {ring.dots.map((dot) => {
                const a = (dot.deg * Math.PI) / 180;
                return (
                  <g key={dot.icon} transform={`translate(${C + ring.r * Math.cos(a)} ${C + ring.r * Math.sin(a)})`}>
                    {/* Counter-rotates against the orbit so the icon stays upright. */}
                    <g className={styles.badge}>
                      <circle cx="0" cy="0" r="36" fill="#E5E3E3" filter="url(#vsBadgeShadow)" />
                      <g
                        transform={`translate(${-12 * SCALE} ${-12 * SCALE}) scale(${SCALE})`}
                        fill="none"
                        stroke="#ee2424"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        {ICONS[dot.icon].map((shape) =>
                          typeof shape === 'string' ? (
                            <path key={shape} d={shape} />
                          ) : (
                            <rect key="rect" x={shape[1]} y={shape[2]} width={shape[3]} height={shape[4]} rx={shape[5]} />
                          ),
                        )}
                      </g>
                    </g>
                  </g>
                );
              })}
            </g>
          ) : null}
        </g>
      ))}

      <g className={styles.float}>
        <g filter="url(#vsShadow)">
          <path fill="#FFFFFF" fillRule="evenodd" transform={`translate(${C} ${C}) scale(.288) translate(-540 -539.5)`} d={SHIELD_PATH} />
        </g>
      </g>
    </svg>
  );
}
